import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { TripService } from '@services/trip.service';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';
import {
  minorsUnlinkedByRemoveFromTripTargets,
  removeFromTripCopyVariant,
  RemoveFromTripTarget,
  showsRemoveFromTripFundraisingWarning,
} from './remove-from-trip-copy';

@Component({
  selector: 'app-remove-from-trip',
  imports: [TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './remove-from-trip.component.html',
  styleUrls: ['./remove-from-trip.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoveFromTripComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);

  readonly participants = computed((): RemoveFromTripTarget[] => {
    const context = this.panelHost.rosterPanelContext();
    if (context?.participants?.length) {
      return context.participants;
    }
    const participant = context?.participant;
    if (participant) {
      return [participant];
    }
    return [];
  });

  readonly isBulk = computed(() => this.participants().length > 1);

  readonly copyVariant = computed(() => {
    const [first] = this.participants();
    return first ? removeFromTripCopyVariant(first) : 'roster';
  });

  readonly showFundraisingWarning = computed(() =>
    showsRemoveFromTripFundraisingWarning(this.copyVariant()),
  );

  readonly unlinkedMinors = computed(() => {
    const roster = this.tripService.rosterTrip()?.roster ?? [];
    return minorsUnlinkedByRemoveFromTripTargets(roster, this.participants());
  });

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'remove-from-trip') {
        this.submitting.set(false);
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  confirm(): void {
    const participants = this.participants();
    const trip = this.tripService.currentTrip.value();
    if (participants.length === 0 || !trip || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.groupManagementService
      .removeParticipants(trip.id, participants.map((p) => p.id))
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              this.isBulk()
                ? marker('group-management.panels.remove-from-trip.success-bulk')
                : marker('group-management.panels.remove-from-trip.success'),
              this.isBulk()
                ? { count: participants.length }
                : { name: participants[0].firstname },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              this.isBulk()
                ? marker('group-management.panels.remove-from-trip.error-bulk')
                : marker('group-management.panels.remove-from-trip.error'),
              this.isBulk()
                ? { count: participants.length }
                : { name: participants[0].firstname },
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }
}
